import React, { useState, useEffect, useMemo } from "react";
import Papa from "papaparse";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";

// Categories (folder names)
const categories = [
  "baby_milk",
  "chicken",
  "diapars_large_size",
  "diapars_small_size",
  "diapars_smallest_size",
  "dried_fish",
  "egg",
  "flour",
  "frozen_fish",
  "fruits",
  "lentil",
  "liquid_milk",
  "meat",
  "oil",
  "other_baby_food",
  "powder_milk",
  "rice",
  "spices",
  "vegetable",
];

// Colors for different years
const yearColors = [
  "#2563eb", "#dc2626", "#059669", "#d97706", "#7c3aed",
  "#db2777", "#0891b2", "#65a30d", "#e11d48", "#7c2d12"
];

// --- helpers ---
const pad2 = (n) => String(n).padStart(2, "0");

// Normalize many common date formats to ISO (YYYY-MM-DD)
function toISODate(str) {
  if (!str) return null;
  const s = String(str).trim();

  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // DD-MM-YYYY or DD/MM/YYYY
  let m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    const [_, dd, mm, yyyy] = m;
    return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
  }
  // MM-DD-YYYY or MM/DD/YYYY
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    const [_, a, b, yyyy] = m;
    // Ambiguous; try best-effort: if looks like US format where a>12 → must be day-first handled above.
    // Here we default to month-first.
    return `${yyyy}-${pad2(a)}-${pad2(b)}`;
  }

  // Fallback: let Date parse and reformat if valid
  const d = new Date(s);
  if (!Number.isNaN(d.valueOf())) {
    const yyyy = d.getFullYear();
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  }
  return null;
}

// Helper to aggregate data by time period
function aggregateByPeriod(data, period) {
  const grouped = {};

  data.forEach(item => {
    let key;
    const date = new Date(item.date);

    switch (period) {
      case 'daily':
        key = item.date;
        break;
      case 'weekly':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
        break;
      case 'monthly':
        key = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;
        break;
      default:
        key = item.date;
    }

    if (!grouped[key]) {
      grouped[key] = { dates: [], prices: [], weights: [] };
    }
    grouped[key].dates.push(item.date);
    grouped[key].prices.push(item.price);
    grouped[key].weights.push(item.weight);
  });

  return Object.entries(grouped)
    .map(([date, group]) => ({
      date,
      price: group.prices.reduce((sum, p) => sum + p, 0) / group.prices.length, // average
      minPrice: Math.min(...group.prices),
      maxPrice: Math.max(...group.prices),
      count: group.prices.length,
      weight: group.weights[0] // Take first weight since they should be the same for grouped data
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

// Helper to prepare multi-year chart data
function prepareMultiYearData(data) {
  if (data.length === 0) return { chartData: [], years: [] };

  // Group data by year
  const yearGroups = {};
  data.forEach(item => {
    const year = new Date(item.date).getFullYear();
    if (!yearGroups[year]) {
      yearGroups[year] = [];
    }
    yearGroups[year].push(item);
  });

  const years = Object.keys(yearGroups).sort();

  // Create a unified timeline based on month-day for comparison across years
  const allDataPoints = new Map();

  years.forEach(year => {
    yearGroups[year].forEach(item => {
      const date = new Date(item.date);
      const monthDay = `${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

      if (!allDataPoints.has(monthDay)) {
        allDataPoints.set(monthDay, { monthDay });
      }

      allDataPoints.get(monthDay)[`price_${year}`] = item.price;
    });
  });

  const chartData = Array.from(allDataPoints.values())
    .sort((a, b) => a.monthDay.localeCompare(b.monthDay));

  return { chartData, years };
}

function App() {
  const [selectedCategory, setSelectedCategory] = useState("egg");
  const [rows, setRows] = useState([]); // normalized rows with isoDate, priceNum, weightKey
  const [uniqueProducts, setUniqueProducts] = useState([]);
  const [selectedProductKey, setSelectedProductKey] = useState("");
  const [aggregation, setAggregation] = useState("monthly"); // daily, weekly, monthly
  const [isLoading, setIsLoading] = useState(true);
  const [isMultiYear, setIsMultiYear] = useState(false); // New state for chart type

  // global date bounds (from all rows in the loaded CSV)
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Load CSV whenever category changes
  useEffect(() => {
    setIsLoading(true);
    Papa.parse(`/api/update-data?file=${selectedCategory}/${selectedCategory}.csv`, {
      // This one for local testing
      // Papa.parse(`./local_data/${selectedCategory}/${selectedCategory}.csv`, {
      download: true,
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (result) => {
        // Normalize & clean
        const normalized = (result.data || [])
          .map((r) => {
            const iso = toISODate(r.date);
            const priceNum = Number(r.price);
            const weightKey = r.weight_raw ? String(r.weight_raw).trim() : "";
            const name = r.product_name ? String(r.product_name).trim() : "";
            return {
              ...r,
              isoDate: iso,
              priceNum,
              weightKey,
              name,
              nameLower: name.toLowerCase(),
            };
          })
          .filter(
            (r) =>
              r.isoDate &&
              !Number.isNaN(r.priceNum) &&
              r.nameLower &&
              r.weightKey
          );

        setRows(normalized);

        // Build unique (product_name lowercased + weight_value) list
        const map = new Map();
        for (const r of normalized) {
          const key = `${r.nameLower}__${r.weightKey}`;
          if (!map.has(key)) {
            map.set(key, { key, label: `${r.name} | Quantity: ${r.weightKey}` });
          }
        }
        const uniques = Array.from(map.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        );
        setUniqueProducts(uniques);

        // Default selected product
        if (uniques.length > 0) {
          setSelectedProductKey(uniques[0].key);
        } else {
          setSelectedProductKey("");
        }

        // Compute global date bounds and set initial range (oldest → newest)
        const dateList = normalized
          .map((r) => r.isoDate)
          .sort((a, b) => new Date(a) - new Date(b));
        if (dateList.length > 0) {
          const min = dateList[0];
          const max = dateList[dateList.length - 1];
          setMinDate(min);
          setMaxDate(max);
          setStartDate(min);
          setEndDate(max);
        } else {
          setMinDate("");
          setMaxDate("");
          setStartDate("");
          setEndDate("");
        }
        setIsLoading(false);
      },
      error: (err) => {
        console.error("CSV parse error:", err);
        setIsLoading(false);
      },
    });
  }, [selectedCategory]);

  // Filtered and aggregated rows for the selected product and date range
  const chartData = useMemo(() => {
    if (!selectedProductKey || !startDate || !endDate) return [];

    const [nameLower, weightKey] = selectedProductKey.split("__");
    const filtered = rows
      .filter(
        (r) =>
          r.nameLower === nameLower &&
          r.weightKey === weightKey &&
          r.isoDate >= startDate &&
          r.isoDate <= endDate
      )
      .map((r) => ({ date: r.isoDate, price: r.priceNum, weight: r.weightKey }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Aggregate the data based on selected period
    return aggregateByPeriod(filtered, aggregation);
  }, [rows, selectedProductKey, startDate, endDate, aggregation]);

  // Prepare multi-year data
  const multiYearData = useMemo(() => {
    if (!isMultiYear) return { chartData: [], years: [] };
    return prepareMultiYearData(chartData);
  }, [chartData, isMultiYear]);

  const yAxisDomain = useMemo(() => {
    const dataToUse = isMultiYear ? multiYearData.chartData : chartData;
    if (dataToUse.length === 0) return [0, 100]; // default range when no data

    let prices = [];
    if (isMultiYear) {
      multiYearData.years.forEach(year => {
        dataToUse.forEach(item => {
          if (item[`price_${year}`] !== undefined) {
            prices.push(item[`price_${year}`]);
          }
        });
      });
    } else {
      prices = dataToUse.map(item => item.price);
    }

    if (prices.length === 0) return [0, 100];

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return [Math.floor(minPrice - 10), Math.ceil(maxPrice + 10)];
  }, [chartData, multiYearData, isMultiYear]);

  // Handlers that keep dates valid & clamped to dataset range
  const onStartDateChange = (v) => {
    if (!v) return;
    const clamped = v < minDate ? minDate : v > maxDate ? maxDate : v;
    setStartDate(clamped);
    if (endDate && clamped > endDate) setEndDate(clamped);
  };
  const onEndDateChange = (v) => {
    if (!v) return;
    const clamped = v < minDate ? minDate : v > maxDate ? maxDate : v;
    setEndDate(clamped);
    if (startDate && clamped < startDate) setStartDate(clamped);
  };

  // Simple tick formatter for dates
  const formatTick = (value) => {
    if (!value) return "";

    if (isMultiYear) {
      // For multi-year view, show month-day format
      const [month, day] = value.split('-');
      const date = new Date(2000, parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    } else {
      // For single line, show date normally
      const d = new Date(value);
      if (Number.isNaN(d.valueOf())) return value;
      return d.toLocaleDateString(undefined, {
        year: '2-digit',
        month: 'short',
      });
    }
  };

  // Get current product name for chart title
  const currentProductName = useMemo(() => {
    if (!selectedProductKey) return "";
    const product = uniqueProducts.find(p => p.key === selectedProductKey);
    return product ? product.label : "";
  }, [selectedProductKey, uniqueProducts]);

  // Generate chart title
  const chartTitle = useMemo(() => {
    const chartType = isMultiYear ? "Multi-Year Price Comparison" : "Price Trend Analysis";
    return `${currentProductName} - ${chartType}`;
  }, [currentProductName, isMultiYear]);

  // Shared input styles for consistency
  const inputStyle = {
    flex: 1,
    padding: "0.5rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    fontSize: "1rem",
    backgroundColor: "#fff",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    minWidth: 0, // Important for flex items to shrink properly
  };

  const labelStyle = {
    display: "flex",
    gap: "0.5rem",
    alignItems: "center",
    fontSize: "0.875rem",
    fontWeight: "500",
  };

  const spanStyle = {
    minWidth: "70px",
    flexShrink: 0,
    textAlign: "left",
  };

  return (
    <>
      <style>
        {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Toggle switch styles */
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }
        
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 24px;
        }
        
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }
        
        input:checked + .toggle-slider {
          background-color: #2563eb;
        }
        
        input:checked + .toggle-slider:before {
          transform: translateX(26px);
        }
        
        /* Ensure consistent styling for date inputs on iOS */
        input[type="date"] {
          -webkit-appearance: none;
          appearance: none;
          background-color: #fff;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          padding: 0.5rem;
          font-size: 1rem;
          min-width: 0;
        }
        
        /* Remove default iOS date picker styling */
        input[type="date"]::-webkit-datetime-edit {
          color: #374151;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator {
          opacity: 1;
          cursor: pointer;
        }
        
        /* Ensure select elements look consistent */
        select {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
          padding-right: 2.5rem;
        }
      `}
      </style>
      <div
        style={{
          padding: "0.75rem",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
          maxWidth: "100%",
          margin: "0",
          boxSizing: "border-box",
          overflow: "hidden", // Prevent any overflow
        }}
      >
        <h1 style={{
          fontSize: "1.125rem",
          marginBottom: "0.25rem",
          lineHeight: "1.3",
          wordBreak: "break-word"
        }}>
          Product Price Trends of Bangladesh{" "}
          <span style={{
            fontSize: "0.75rem",
            fontWeight: "normal",
            display: "block",
            marginTop: "0.25rem"
          }}>
            Based on data from{" "}
            <a
              href="https://chaldal.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb", textDecoration: "underline" }}
            >
              chaldal.com
            </a>
          </span>
        </h1>

        <p style={{
          fontSize: "0.7rem",
          color: "#6b7280",
          marginTop: "0",
          marginBottom: "1rem",
          lineHeight: "1.4"
        }}>
          For downloading raw data check this{" "}
          <a
            href="https://github.com/Shayokh144/Commodity_Price_Analysis_BD/tree/main/data/price_data"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#2563eb", textDecoration: "underline" }}
          >
            repository
          </a>
        </p>

        {/* Controls container - fully responsive */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            marginBottom: "1rem",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Category */}
          <label style={labelStyle}>
            <span style={spanStyle}>Category</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={inputStyle}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>

          {/* Product */}
          <label style={labelStyle}>
            <span style={spanStyle}>Product</span>
            <select
              value={selectedProductKey}
              onChange={(e) => setSelectedProductKey(e.target.value)}
              style={inputStyle}
            >
              {uniqueProducts.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          {/* Aggregation Period */}
          <label style={labelStyle}>
            <span style={spanStyle}>View By</span>
            <select
              value={aggregation}
              onChange={(e) => setAggregation(e.target.value)}
              style={inputStyle}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>

          {/* Date range - two columns on mobile */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.5rem",
              width: "100%",
            }}
          >
            <label style={{
              ...labelStyle,
              flexDirection: "column",
              alignItems: "stretch",
              gap: "0.25rem"
            }}>
              <span style={{ fontSize: "0.75rem", fontWeight: "500" }}>Start Date</span>
              <input
                type="date"
                value={startDate || ""}
                min={minDate || ""}
                max={maxDate || ""}
                onChange={(e) => onStartDateChange(e.target.value)}
                style={{
                  ...inputStyle,
                  flex: "none",
                  fontSize: "0.875rem"
                }}
              />
            </label>
            <label style={{
              ...labelStyle,
              flexDirection: "column",
              alignItems: "stretch",
              gap: "0.25rem"
            }}>
              <span style={{ fontSize: "0.75rem", fontWeight: "500" }}>End Date</span>
              <input
                type="date"
                value={endDate || ""}
                min={minDate || ""}
                max={maxDate || ""}
                onChange={(e) => onEndDateChange(e.target.value)}
                style={{
                  ...inputStyle,
                  flex: "none",
                  fontSize: "0.875rem"
                }}
              />
            </label>
          </div>

          {/* Chart Type Toggle */}
          <label style={{
            ...labelStyle,
            cursor: "pointer"
          }}>
            <span style={spanStyle}>Multi-Year View</span>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isMultiYear}
                  onChange={(e) => setIsMultiYear(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </label>
        </div>

        {/* Chart Title */}
        {chartTitle && (
          <h2 style={{
            fontSize: "1rem",
            fontWeight: "600",
            margin: "0 0 0.5rem 0",
            textAlign: "center",
            color: "#374151"
          }}>
            {chartTitle}
          </h2>
        )}

        {/* Info text */}
        {minDate && maxDate && (
          <div style={{
            fontSize: "0.75rem",
            color: "#1c18dbff",
            lineHeight: "1.4",
            wordBreak: "break-word",
            marginBottom: "1rem",
            textAlign: "center"
          }}>
            Available range: <strong>{minDate}</strong> to{" "}
            <strong>{maxDate}</strong> |
            Showing {isMultiYear ? multiYearData.chartData.length : chartData.length} data points
            {aggregation !== 'daily' && ` (${aggregation} averages)`}
            {isMultiYear && multiYearData.years.length > 0 &&
              ` across ${multiYearData.years.length} years`
            }
          </div>
        )}

        {/* Chart area */}
        <div
          style={{
            width: "100%",
            height: "50vh",
            minHeight: "250px",
            maxHeight: "400px",
            touchAction: "pan-y",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "0.5rem",
          }}
        >
          {isLoading ? (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              color: "#666"
            }}>
              <div style={{ fontSize: "0.875rem" }}>📊 Loading chart data...</div>
              <div style={{
                width: "24px",
                height: "24px",
                border: "3px solid #f3f3f3",
                borderTop: "3px solid #2563eb",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }}></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={isMultiYear ? multiYearData.chartData : chartData}
                margin={{ top: 16, right: 8, bottom: 16, left: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey={isMultiYear ? "monthDay" : "date"}
                  tickFormatter={formatTick}
                  minTickGap={20}
                  stroke="#666"
                  tick={{
                    fill: '#333',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}
                  label={{
                    value: isMultiYear ? '' : 'Timestamp',
                    position: 'insideBottom',
                    offset: -12,
                    style: {
                      fill: '#483ac3ff',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }
                  }}
                />
                <YAxis
                  stroke="#666"
                  label={{
                    value: 'Price in BDT',
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      fill: '#483ac3ff',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }
                  }}
                  tick={{
                    fill: '#333',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                  domain={yAxisDomain}
                />
                <Tooltip
                  wrapperStyle={{
                    fontSize: "0.75rem",
                    backgroundColor: "#fff",
                    border: "1px solid #154627ff",
                    borderRadius: "4px"
                  }}
                  isAnimationActive={false}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          padding: "8px",
                          fontSize: "0.7rem",
                          maxWidth: "200px"
                        }}>
                          <div style={{ marginBottom: "4px", fontWeight: "bold" }}>
                            {isMultiYear ? `Date: ${label}` : new Date(label).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "2-digit",
                            })}
                          </div>
                          {payload.map((entry, index) => (
                            <div key={index} style={{ color: entry.color }}>
                              {isMultiYear
                                ? `${entry.name}: ${entry.value.toFixed(2)} BDT`
                                : `Price: ${entry.value.toFixed(2)} BDT`
                              }
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {isMultiYear && <Legend wrapperStyle={{ paddingTop: '10px' }} />}
                {isMultiYear ? (
                  // Multi-year lines
                  multiYearData.years.map((year, index) => (
                    <Line
                      key={year}
                      type="monotone"
                      dataKey={`price_${year}`}
                      stroke={yearColors[index % yearColors.length]}
                      strokeWidth={2}
                      dot={false}
                      name={year}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  ))
                ) : (
                  // Single line
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={chartData.length <= 50 ? { r: 3, fill: "#2563eb" } : false}
                    activeDot={{ r: 5, fill: "#1d4ed8" }}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Empty-state helper */}
        {!isLoading && (isMultiYear ? multiYearData.chartData.length === 0 : chartData.length === 0) && (
          <div style={{
            marginTop: "0.5rem",
            fontSize: "0.75rem",
            color: "#888",
            textAlign: "center",
            padding: "1rem"
          }}>
            No data for the selected product and date range.
          </div>
        )}
      </div>
    </>
  );
}

export default App;